import { type Recipe } from '@/types/recipe'
import { useSettingsStore, type GPTModel } from '@/stores/settings-store'

export interface AIRecipeIdea {
  id: string
  title: string
  description: string
  why_it_fits?: string
  prep_time?: number
  cook_time?: number
  servings?: number
  difficulty?: Recipe['difficulty']
  meal_type?: Recipe['meal_type']
  tags?: string[]
  /** The real recipe this generated dish is based on. */
  anchor_id?: string
  anchor_title?: string
}

export interface AIRecipeParseResult {
  success: boolean
  recipe?: Partial<Recipe>
  error?: string
}

export interface AIRecipeIdeasResult {
  success: boolean
  ideas?: AIRecipeIdea[]
  error?: string
}

export interface MenuCandidateSummary {
  id: string
  title: string
  description?: string
  tags?: string[]
  total_time?: number
  difficulty?: Recipe['difficulty']
}

/** A real, highly-rated recipe used as reference material for new dishes. */
export interface MenuAnchorSummary {
  id: string
  title: string
  rating?: number
  tags?: string[]
  key_ingredients?: string[]
}

export interface AIWeekMenuSelection {
  id: string
  reason?: string
}

export interface AIWeekMenuResult {
  success: boolean
  selection?: AIWeekMenuSelection[]
  ideas?: AIRecipeIdea[]
  error?: string
}

interface AIStatusResponse {
  configured: boolean
}

type AIRequestBody =
  | {
      action: 'parse-text'
      inputText: string
      model: GPTModel
    }
  | {
      action: 'parse-url'
      url: string
      model: GPTModel
    }
  | {
      action: 'refine-recipe'
      recipe: Partial<Recipe>
      instruction: string
      model: GPTModel
    }
  | {
      action: 'generate-ideas'
      prompt: string
      model: GPTModel
    }
  | {
      action: 'expand-idea'
      idea: Omit<AIRecipeIdea, 'id'>
      prompt: string
      model: GPTModel
    }
  | {
      action: 'curate-menu'
      recipes: MenuCandidateSummary[]
      anchors: MenuAnchorSummary[]
      count: number
      ideaCount: number
      preferences?: string
      model: GPTModel
    }

const STATUS_CACHE_KEY = 'ai-recipe-parser-status'
const STATUS_CACHE_TTL_MS = 10 * 60 * 1000

class AIRecipeParserService {
  private statusCache: boolean | null = null

  private getModel(): GPTModel {
    return useSettingsStore.getState().gptModel || 'gpt-5.4-mini'
  }

  private async request<T>(input: RequestInfo | URL, init?: RequestInit): Promise<T> {
    const response = await fetch(input, {
      headers: {
        'Content-Type': 'application/json',
        ...(init?.headers ?? {}),
      },
      ...init,
    })

    const data = (await response.json()) as T & { error?: string }

    if (!response.ok) {
      throw new Error(data.error || 'AI verzoek mislukt')
    }

    return data
  }

  private readPersistedStatus(): boolean | null {
    if (typeof window === 'undefined') {
      return null
    }

    try {
      const raw = window.sessionStorage.getItem(STATUS_CACHE_KEY)
      if (!raw) {
        return null
      }

      const parsed = JSON.parse(raw) as { configured: boolean; timestamp: number }
      if (Date.now() - parsed.timestamp > STATUS_CACHE_TTL_MS) {
        window.sessionStorage.removeItem(STATUS_CACHE_KEY)
        return null
      }

      return parsed.configured
    } catch {
      return null
    }
  }

  private persistStatus(configured: boolean) {
    if (typeof window === 'undefined') {
      return
    }

    window.sessionStorage.setItem(
      STATUS_CACHE_KEY,
      JSON.stringify({ configured, timestamp: Date.now() })
    )
  }

  peekStatus(): boolean | null {
    if (this.statusCache !== null) {
      return this.statusCache
    }

    const persistedStatus = this.readPersistedStatus()
    if (persistedStatus !== null) {
      this.statusCache = persistedStatus
    }

    return this.statusCache
  }

  async getStatus(forceRefresh = false): Promise<boolean> {
    if (!forceRefresh) {
      const cachedStatus = this.peekStatus()
      if (cachedStatus !== null) {
        return cachedStatus
      }
    }

    try {
      const data = await this.request<AIStatusResponse>('/api/ai/recipe-parser', {
        method: 'GET',
      })
      this.statusCache = data.configured
      this.persistStatus(data.configured)
      return data.configured
    } catch {
      this.statusCache = false
      this.persistStatus(false)
      return false
    }
  }

  async parseRecipeText(text: string): Promise<AIRecipeParseResult> {
    return this.runRecipeAction({
      action: 'parse-text',
      inputText: text,
      model: this.getModel(),
    })
  }

  async parseRecipeFromUrl(url: string): Promise<AIRecipeParseResult> {
    return this.runRecipeAction({
      action: 'parse-url',
      url,
      model: this.getModel(),
    })
  }

  async refineRecipe(recipe: Partial<Recipe>, instruction: string): Promise<AIRecipeParseResult> {
    return this.runRecipeAction({
      action: 'refine-recipe',
      recipe,
      instruction,
      model: this.getModel(),
    })
  }

  async generateRecipeIdeas(prompt: string): Promise<AIRecipeIdeasResult> {
    const configured = await this.getStatus()
    if (!configured) {
      return {
        success: false,
        error: 'AI is nog niet platformbreed geconfigureerd. Voeg OPENAI_API_KEY toe in Vercel.',
      }
    }

    try {
      const result = await this.request<AIRecipeIdeasResult>('/api/ai/recipe-parser', {
        method: 'POST',
        body: JSON.stringify({
          action: 'generate-ideas',
          prompt,
          model: this.getModel(),
        } satisfies AIRequestBody),
      })

      return {
        ...result,
        ideas: result.ideas?.map((idea, index) => ({
          ...idea,
          id: idea.id || `idea-${index + 1}`,
        })),
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Onbekende fout bij inspiratievoorstellen',
      }
    }
  }

  async curateWeekMenu(
    recipes: MenuCandidateSummary[],
    options: {
      anchors?: MenuAnchorSummary[]
      count?: number
      ideaCount?: number
      preferences?: string
    } = {}
  ): Promise<AIWeekMenuResult> {
    const configured = await this.getStatus()
    if (!configured) {
      return {
        success: false,
        error: 'AI is nog niet platformbreed geconfigureerd. Voeg OPENAI_API_KEY toe in Vercel.',
      }
    }

    try {
      const result = await this.request<AIWeekMenuResult>('/api/ai/recipe-parser', {
        method: 'POST',
        body: JSON.stringify({
          action: 'curate-menu',
          recipes,
          anchors: options.anchors ?? [],
          count: options.count ?? 2,
          ideaCount: options.ideaCount ?? 4,
          preferences: options.preferences,
          model: this.getModel(),
        } satisfies AIRequestBody),
      })

      return {
        ...result,
        ideas: result.ideas?.map((idea, index) => ({
          ...idea,
          id: idea.id || `menu-idea-${index + 1}`,
        })),
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Onbekende fout bij het samenstellen van het weekmenu',
      }
    }
  }

  async expandRecipeIdea(
    idea: Omit<AIRecipeIdea, 'id'>,
    prompt: string
  ): Promise<AIRecipeParseResult> {
    return this.runRecipeAction({
      action: 'expand-idea',
      idea,
      prompt,
      model: this.getModel(),
    })
  }

  private async runRecipeAction(body: AIRequestBody): Promise<AIRecipeParseResult> {
    const configured = await this.getStatus()
    if (!configured) {
      return {
        success: false,
        error: 'AI is nog niet platformbreed geconfigureerd. Voeg OPENAI_API_KEY toe in Vercel.',
      }
    }

    try {
      const result = await this.request<AIRecipeParseResult>('/api/ai/recipe-parser', {
        method: 'POST',
        body: JSON.stringify(body),
      })

      return result
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Onbekende fout bij AI parser',
      }
    }
  }
}

export const aiRecipeParser = new AIRecipeParserService()
